import { useCallback, useState, useMemo } from 'react';
import { SubmissionMode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { DigitalFormsMutations } from '@frontend/api-digital-forms';
import { FormsFormBuilder, FormsLibrary } from '@frontend/api-forms';
import { PersonAPI, PersonsV3 } from '@frontend/api-person';
import { DigitalFormScopeHooks } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { Attachment, Category, FormOrPacketToSend } from '../types/form-packet-selector.types';
import { getFormattedPDFField, getTransientAttachmentsFormData } from '../utils/form-builder.utils';

interface GenerateSolicitedLinkPayload {
  attachments: Attachment[];
  appointmentId?: string;
  formOrPacketToSend: FormOrPacketToSend;
  deliveryMethod: string;
  officeNote?: {
    label: string;
    value: string;
    notesBy: string;
  };
}

interface GenerateSolicitedLinkResult {
  linkToSend: string;
  submissionToken: string;
}

interface UseSolicitedLinkProps {
  locationId: string;
  personId: string;
}

interface UseSolicitedLinkResults {
  generateTransientForm: (files: Attachment[]) => Promise<{ success: boolean; formId: string }>;
  generateSolicitedLink: (payload: GenerateSolicitedLinkPayload) => Promise<GenerateSolicitedLinkResult>;
  isGeneratingSolicitedLink: boolean;
}

export const useSolicitedLink = ({ locationId, personId }: UseSolicitedLinkProps): UseSolicitedLinkResults => {
  const { t } = useTranslation('forms');
  const [isGeneratingSolicitedLink, setIsGeneratingSolicitedLink] = useState(false);
  const alert = useAlert();
  const locationIds = useMemo(() => [locationId], [locationId]);
  const { refetch: fetchPerson } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: personId ?? '',
      locationIds,
    },
    {
      enabled: false,
    }
  );

  const { hasPermissionToReviewSubmissions } = DigitalFormScopeHooks.useFormsACL({ locationId });
  const { mutateAsync: createSolicitedLink } = DigitalFormsMutations.useCreateFormLinks();
  const { mutateAsync: updateReviewRequiredFlagForPacket } =
    DigitalFormsMutations.useUpdateReviewRequiredFlagForPacket();
  const { mutateAsync: updateReviewRequiredFlagForForm } = DigitalFormsMutations.useUpdateReviewRequiredFlagForForm();

  /**
   * Generates a transient form with the provided files.
   */
  const generateTransientForm = useCallback(
    async (files: Attachment[]) => {
      if (files.length === 0) {
        return {
          success: true,
          formId: '',
        };
      }

      const pdfFormFields = files.map(({ eSignRequired, id, mediaUploadId, file }) =>
        getFormattedPDFField({
          fieldId: id,
          label: file.name,
          value: mediaUploadId,
          eSignRequired,
        })
      );

      const transientFormPayload = getTransientAttachmentsFormData({
        fields: pdfFormFields,
        formName: 'Review Document',
        locationId,
      });

      try {
        const response = await FormsFormBuilder.API.saveFormTemplate({ data: transientFormPayload });
        return {
          success: true,
          formId: response.formId ?? '',
        };
      } catch {
        alert.error(t('Error occurred while generating transient form!'));
        return {
          success: false,
          formId: '',
        };
      }
    },
    [locationId, alert, t]
  );

  /**
   * Generates a solicited link for the selected form/packet.
   */
  async function generateSolicitedLink({
    attachments,
    appointmentId,
    formOrPacketToSend,
    deliveryMethod,
    officeNote,
  }: GenerateSolicitedLinkPayload): Promise<GenerateSolicitedLinkResult> {
    setIsGeneratingSolicitedLink(true);

    try {
      // Validate personId before making API calls
      if (!personId) {
        throw new Error(t('Error: No person selected'));
      }

      const personQueryResponse = await fetchPerson();

      const fetchedPerson = personQueryResponse.data;

      if (!fetchedPerson) {
        throw new Error(t('Error occurred while fetching person data!'));
      }

      // Fetch household data directly with the actual householdId
      let household: any[] = [];
      if (fetchedPerson.householdId) {
        try {
          household = await PersonAPI.getPersonHousehold(fetchedPerson.householdId, {
            headers: { 'Location-Id': locationId },
          });
        } catch (error) {
          console.warn('[generateSolicitedLink] Failed to fetch household data:', error);
          // Household fetch is optional, continue without it
        }
      }

      let documentIdForSolicitedLink: string = formOrPacketToSend.id;
      let isTransientPacket = false;

      // Check if transient form should be created.
      if (attachments.length > 0) {
        isTransientPacket = true;
        const transientFormResponse = await generateTransientForm(attachments);

        if (!transientFormResponse.success) {
          return {
            linkToSend: '',
            submissionToken: '',
          };
        }

        const transientFormId = transientFormResponse.formId;
        const formIdsForTransientPacket: string[] = [];

        switch (formOrPacketToSend.type) {
          case Category.FORMS: {
            formIdsForTransientPacket.push(formOrPacketToSend.id, transientFormId);
            break;
          }

          case Category.PACKETS: {
            formIdsForTransientPacket.push(...formOrPacketToSend.formIds, transientFormId);
            break;
          }
        }

        try {
          // Create a transient packet with the transient form in it
          const response = await FormsLibrary.API.createPacket({
            forms_ids: formIdsForTransientPacket,
            name: formOrPacketToSend.name,
            is_transient: true,
            locationId,
          });

          documentIdForSolicitedLink = response.packetId;

          // Update review required status for the transient packet if the original form/packet requires review
          if (formOrPacketToSend.reviewRequired) {
            await updateReviewRequiredFlagForPacket({
              companyId: locationId,
              packetId: documentIdForSolicitedLink,
              reviewRequired: true,
            });
          }
        } catch {
          alert.error(t('Error occurred while creating transient packet!'));
          return {
            linkToSend: '',
            submissionToken: '',
          };
        }
      }

      /**
       * If the user has permission to review submissions, then the solicited link should be generated with
       * the review flag set to true
       */
      if (hasPermissionToReviewSubmissions) {
        if (!formOrPacketToSend.reviewRequired) {
          if (isTransientPacket) {
            await updateReviewRequiredFlagForPacket({
              companyId: locationId,
              packetId: documentIdForSolicitedLink,
              reviewRequired: true,
            });
          } else {
            switch (formOrPacketToSend.type) {
              case Category.FORMS: {
                await updateReviewRequiredFlagForForm({
                  companyId: locationId,
                  formId: documentIdForSolicitedLink,
                  reviewRequired: true,
                });
                break;
              }

              case Category.PACKETS: {
                await updateReviewRequiredFlagForPacket({
                  companyId: locationId,
                  packetId: documentIdForSolicitedLink,
                  reviewRequired: true,
                });
                break;
              }
            }
          }
        }
      }

      try {
        const solicitedLinkResponse = await createSolicitedLink({
          companyId: locationId,
          patients: personId
            ? [
                {
                  id: personId,
                  firstName: fetchedPerson?.preferredName || fetchedPerson.firstName,
                  lastName: fetchedPerson.lastName,
                  birthdate: fetchedPerson.birthdate,
                  email: fetchedPerson.contactInfo?.find((c) => c.type === ContactType_Enum.EMAIL)?.destination,
                  appointmentId,
                },
              ]
            : [],
          sendId: documentIdForSolicitedLink,
          type: formOrPacketToSend.type === Category.PACKETS || isTransientPacket ? 'packet' : 'form',
          familyHead: household.find((h) => h.is_guardian)?.id,
          mode: deliveryMethod == 'kiosk' ? SubmissionMode.SOLICITED_OFFICEKIOSK : SubmissionMode.SOLICITED_DIRECT,
          ...(officeNote && { officeNote }),
        });

        const linkToSend = solicitedLinkResponse.data?.find((link) => !!link.submitUrl)?.submitUrl ?? '';
        const submissionToken = solicitedLinkResponse.data?.find((link) => !!link.token)?.token ?? '';
        return {
          linkToSend,
          submissionToken,
        };
      } catch {
        alert.error(t('Error occurred while generating solicited link!'));
        return {
          linkToSend: '',
          submissionToken: '',
        };
      }
    } finally {
      setIsGeneratingSolicitedLink(false);
    }
  }

  return {
    generateTransientForm,
    generateSolicitedLink,
    isGeneratingSolicitedLink,
  };
};
